import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Baie coulissante', to: '/reparation-baie-coulissante-herault/' },
  { label: 'Remplacement baie' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Remplacement de baie coulissante aluminium dans l\'Hérault',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'AdministrativeArea', name: 'Hérault' },
  description: 'Remplacement de baie coulissante aluminium sur mesure avec double vitrage. Pose par nos techniciens dans tout l\'Hérault.',
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Quand faut-il remplacer une baie coulissante plutôt que la réparer ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Le remplacement est recommandé lorsque le cadre aluminium est déformé, que le vitrage est ancien et peu isolant, ou que le coût cumulé des réparations approche celui d\'une baie neuve. Nos techniciens vous conseillent en toute transparence : si la réparation est possible et durable, nous la privilégions. Si le remplacement est plus pertinent, nous vous accompagnons dans le choix de la nouvelle baie.',
      },
    },
    {
      '@type': 'Question',
      name: 'Quel est le délai pour le remplacement d\'une baie coulissante ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Après la prise de mesures et la validation du devis, la fabrication sur mesure prend généralement 2 à 4 semaines selon le fabricant. La pose elle-même s\'effectue en une journée. Nous gérons l\'intégralité du processus, de la dépose de l\'ancienne baie à l\'évacuation des déchets, en passant par les finitions.',
      },
    },
    {
      '@type': 'Question',
      name: 'Proposez-vous des baies coulissantes à rupture de pont thermique ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, nous proposons des baies coulissantes aluminium à rupture de pont thermique, qui offrent une excellente isolation tout en conservant l\'esthétique et la finesse des profilés aluminium. Le vitrage est en double vitrage à isolation renforcée, conforme aux normes thermiques actuelles.',
      },
    },
  ],
};

const contentBlocks = [
  {
    image: '/images/remplacement-block1.jpg',
    imageAlt: 'Ancienne baie coulissante a remplacer dans l Herault',
    children: (
      <>
        <h2>Quand la réparation ne suffit plus</h2>
        <p>
          Il arrive un moment où la réparation d'une baie coulissante n'est plus la solution la plus pertinente. Un cadre aluminium déformé par un choc ou par des décennies d'exposition aux intempéries, un vitrage simple d'ancienne génération qui laisse passer le froid l'hiver et la chaleur l'été, ou une menuiserie dont les pièces détachées ne sont plus disponibles : autant de situations où le remplacement complet s'impose. DFPRO Hérault vous accompagne dans le choix et la pose de votre nouvelle baie coulissante, à Montpellier comme dans toutes les communes de l'Hérault.
        </p>
        <h3>Quand envisager le remplacement ?</h3>
        <p>
          Plusieurs indicateurs objectifs plaident pour un remplacement. Une facture énergétique qui grimpe, des courants d'air persistants près de la baie, une condensation régulière entre les vitres témoignant d'un double vitrage défaillant, ou encore un dormant endommagé qui ne peut plus assurer l'étanchéité. L'âge de la menuiserie est aussi un critère : une baie coulissante de plus de 25 ans, même bien entretenue, n'offre pas les performances thermiques et phoniques des modèles contemporains. Nos techniciens réalisent un diagnostic objectif : si la <Link to="/reparation-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réparation</Link> est la meilleure option, nous la recommandons. Si le remplacement est plus judicieux, nous vous l'expliquons clairement.
        </p>
      </>
    ),
  },
  {
    image: '/images/remplacement-block2.jpg',
    imageAlt: 'Technicien DFPRO installant une nouvelle baie coulissante aluminium',
    imagePosition: 'right' as const,
    children: (
      <>
        <h3>Notre processus de remplacement</h3>
        <p>
          Le remplacement d'une baie coulissante se déroule en plusieurs étapes. Nous commençons par une prise de mesures précises de l'existant, en tenant compte des contraintes du bâti — notamment dans l'habitat ancien du centre-ville de Montpellier ou de Sète où les ouvertures ne sont pas toujours standard. Nous vous présentons ensuite une sélection de baies coulissantes aluminium sur mesure, avec différents niveaux de performances thermiques et phoniques, pour que vous puissiez choisir en fonction de votre budget et de vos priorités. Une fois le devis validé, la baie est fabriquée sur mesure. La pose est effectuée en une journée par nos techniciens : dépose de l'ancienne menuiserie, préparation de l'ouverture, pose de la nouvelle baie, calfeutrement, réglages et finitions. Nous évacuons l'ancienne baie et les déchets de chantier.
        </p>
        <h3>Des baies aluminium performantes</h3>
        <p>
          Les baies coulissantes que nous installons sont en aluminium à rupture de pont thermique, équipées de double vitrage à isolation renforcée. Les profilés fins conservent l'esthétique contemporaine de l'aluminium tout en offrant une isolation thermique excellente, conforme aux exigences actuelles. Nous travaillons avec les principaux fabricants français pour vous proposer des menuiseries de qualité, durables et garanties. Chaque baie est fabriquée aux dimensions exactes de votre ouverture, ce qui permet une intégration parfaite et une étanchéité optimale.
        </p>
        <h3>Un investissement durable</h3>
        <p>
          Remplacer une baie coulissante est un investissement qui se justifie sur le long terme. Les économies d'énergie réalisées grâce à une isolation performante, le confort accru (absence de courants d'air, isolation phonique renforcée), et la valorisation de votre bien immobilier compensent largement le coût initial. De plus, une baie neuve ne nécessitera aucun entretien particulier pendant de nombreuses années. Dans l'Hérault, où les étés sont chauds et les hivers parfois venteux sur le littoral, une baie performante change véritablement le confort de vie.
        </p>
        <p>
          Pour toute question sur le remplacement de votre baie coulissante ou pour obtenir un devis gratuit, appelez-nous au 09 80 80 22 42. Retrouvez également nos autres services sur la <Link to="/reparation-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réparation de baie coulissante</Link>.
        </p>
      </>
    ),
  },
];

export default function RemplacementBaie() {
  return (
    <>
      <SEO
        title="Remplacement de baie coulissante alu dans l'Hérault | DFPRO"
        description="Remplacement de baie coulissante aluminium sur mesure dans l'Hérault. Double vitrage, pose comprise. Devis gratuit, garantie 10 ans. 09 80 80 22 42."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/remplacement-baie-coulissante-herault/"
      />
      <ServicePageLayout
        title="Remplacement de baie vitrée coulissante dans l'Hérault"
        subtitle="Votre baie coulissante est trop endommagée ? Nous la remplaçons par un modèle aluminium sur mesure avec double vitrage, dans tout l'Hérault."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        heroImage="/images/remplacement-hero.jpg"
        heroImageAlt="Remplacement de baie coulissante aluminium DFPRO Herault"
        contentBlocks={contentBlocks}
      />
    </>
  );
}